/**
 * FwmHero — the one hero every public FWM section wears: a full-bleed
 * photograph of the ministry itself under a dark scrim, an eyebrow, a serif
 * headline with a single gold phrase, and exactly one gold button.
 *
 * FwmStatStrip sits underneath and overlaps the photograph, which is what
 * gives the page its depth without adding another colour.
 */
import React from 'react';

// Background video is desktop/tablet only — on a phone it is the still image,
// so nobody burns their data on a decorative loop.
const useWideScreen = () => {
  const [wide, setWide] = React.useState(
    () => typeof window !== 'undefined'
      && window.matchMedia('(min-width: 768px)').matches,
  );
  React.useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)');
    const onChange = (e) => setWide(e.matches);
    query.addEventListener('change', onChange);
    setWide(query.matches);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return wide;
};

export const FwmHero = ({
  image, video, mediaPriority = 'image', eyebrow, title, accent, blurb, actions,
  align = 'end', testId = 'fwm-hero', children, fallbackImage = '',
}) => {
  const wide = useWideScreen();
  const [broken, setBroken] = React.useState(false);
  const [imageBroken, setImageBroken] = React.useState(false);
  // iter 636 — when an uploaded banner will not load (object storage down, the
  // file withdrawn), fall back to the picture the app ships with before giving
  // up on a gradient. A church page with no photo reads as broken.
  const [usingFallback, setUsingFallback] = React.useState(false);
  React.useEffect(() => setBroken(false), [video]);
  React.useEffect(() => { setImageBroken(false); setUsingFallback(false); }, [image]);
  const shown = usingFallback ? fallbackImage : image;
  const onImageError = () => {
    if (!usingFallback && fallbackImage && fallbackImage !== image) {
      setUsingFallback(true);
    } else {
      setImageBroken(true);
    }
  };
  const showVideo = Boolean(video) && mediaPriority === 'video' && wide && !broken;
  return (
  <section className="fwm-hero" data-testid={testId}>
    {showVideo ? (
      <video className="fwm-hero-photo" src={video} poster={image || undefined}
             data-testid={`${testId}-video`} onError={() => setBroken(true)}
             autoPlay muted loop playsInline preload="metadata" />
    ) : (shown && !imageBroken) ? (
      <img src={shown} alt="" aria-hidden="true" fetchPriority="high"
           data-testid={`${testId}-image`}
           className="fwm-hero-photo" onError={onImageError} />
    ) : (
      <div className="fwm-hero-photo" data-testid={`${testId}-image-fallback`}
           style={{ background: 'linear-gradient(135deg,#14110F 0%,#2A2118 58%,#8A6717 140%)' }} />
    )}
    <div className="fwm-hero-scrim" />
    <div className={`fwm-hero-inner ${align === 'center' ? 'items-center text-center' : ''}`}>
      {eyebrow && (
        <p className="fwm-hero-eyebrow fwm-rise" data-testid={`${testId}-eyebrow`}>{eyebrow}</p>
      )}
      <h1 className="fwm-hero-title fwm-rise fwm-rise-1" data-testid={`${testId}-title`}>
        {title}
        {accent && <><br /><span className="fwm-hero-accent">{accent}</span></>}
      </h1>
      {blurb && (
        <p className="fwm-hero-blurb fwm-rise fwm-rise-2" data-testid={`${testId}-blurb`}>{blurb}</p>
      )}
      {actions && <div className="fwm-hero-actions fwm-rise fwm-rise-3">{actions}</div>}
      {children}
    </div>
  </section>
  );
};

export const FwmStatStrip = ({ stats = [], testId = 'fwm-stat-strip' }) => (
  <div className="fwm-stat-wrap" data-testid={testId}>
    <div className="fwm-stat-strip">
      {stats.map(([value, label], index) => (
        <div key={label} className="fwm-stat fwm-rise" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
             style={{ animationDelay: `${180 + index * 70}ms` }}>
          <p className="fwm-stat-n">{value}</p>
          <p className="fwm-stat-l">{label}</p>
        </div>
      ))}
    </div>
  </div>
);

export default FwmHero;
